import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { RefreshCw, AlertTriangle, CheckCircle2, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import ProjectDetailModal from './ProjectDetailModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EstimateInvoicePaid = ({ globalFY, getAuthHeaders, formatCurrency }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const fmt = (v) => formatCurrency(v);

  const fetchData = useCallback(async () => {
    if (!globalFY) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/pnl/estimate-invoice-paid?financial_year=${globalFY}`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load estimate→invoice→paid reconciliation');
    } finally {
      setLoading(false);
    }
  }, [globalFY, getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return <div className="flex justify-center py-16" data-testid="eip-loading"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div>;
  }
  if (!data) return null;

  const s = data.summary;
  const rows = onlyFlagged ? data.rows.filter(r => r.flagged) : data.rows;

  return (
    <div className="space-y-6" data-testid="estimate-invoice-paid">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ArrowRightLeft className="w-6 h-6 text-indigo-600" />Estimate → Invoice → Paid</h2>
          <p className="text-sm text-slate-500">Traces each project's estimate to its invoice, payments and TDS withheld · FY {globalFY}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={onlyFlagged ? 'default' : 'outline'} onClick={() => setOnlyFlagged(v => !v)} data-testid="eip-toggle-flagged">
            <AlertTriangle className="w-4 h-4 mr-1" />{onlyFlagged ? 'Showing issues' : 'Only issues'}
          </Button>
          <Button variant="outline" size="icon" onClick={fetchData} data-testid="eip-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="eip-summary">
        <Chip label="Projects" value={s.projects} />
        <Chip label="Estimate Rev" value={fmt(s.total_estimate_revenue)} money />
        <Chip label="Invoice Total" value={fmt(s.total_invoice_total)} money />
        <Chip label="Paid" value={fmt(s.total_paid)} money />
        <Chip label="TDS" value={fmt(s.total_tds)} money />
        <Chip label="Balance" value={fmt(s.total_balance)} money danger={s.total_balance > 0} />
        <Chip label="Flagged" value={s.flagged} danger={s.flagged > 0} />
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <Table data-testid="eip-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>CRM ID</TableHead>
              <TableHead>Project</TableHead>
              <TableHead className="text-right">Estimate</TableHead>
              <TableHead className="text-right">Inv Taxable</TableHead>
              <TableHead className="text-right">GST</TableHead>
              <TableHead className="text-right">Invoice Total</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">TDS</TableHead>
              <TableHead className="text-center">TDS %</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead className="text-center">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={11} className="text-center text-slate-400 py-8">Nothing to reconcile</TableCell></TableRow>
            )}
            {rows.map((r, i) => (
              <TableRow
                key={r.crm_project_id || i}
                className={`cursor-pointer hover:bg-slate-50 ${r.crm_project_id ? '' : 'opacity-70'}`}
                onClick={() => r.crm_project_id && setDetailId(r.crm_project_id)}
                data-testid={`eip-row-${r.crm_project_id || i}`}
              >
                <TableCell className="font-mono text-xs">
                  {r.crm_project_id || '—'}
                  {r.crm_project_id && !r.in_crm && <Badge className="bg-red-100 text-red-700 hover:bg-red-100 ml-1 text-[10px]">no CRM</Badge>}
                </TableCell>
                <TableCell className="max-w-[200px] truncate">
                  {r.training_name || '—'}
                  {r.is_foreign && <Badge variant="outline" className="ml-1 text-[10px] text-amber-700 border-amber-300">{r.currency}</Badge>}
                </TableCell>
                <TableCell className="text-right tabular-nums">{fmt(r.estimate_revenue)}</TableCell>
                <TableCell className="text-right tabular-nums">{r.not_invoiced ? <span className="text-slate-400">—</span> : fmt(r.invoice_taxable)}</TableCell>
                <TableCell className="text-right tabular-nums text-slate-500">{r.not_invoiced ? '—' : fmt(r.invoice_gst)}</TableCell>
                <TableCell className="text-right tabular-nums">{r.not_invoiced ? <span className="text-slate-400">no invoice</span> : fmt(r.invoice_total)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmt(r.paid)}</TableCell>
                <TableCell className="text-right tabular-nums text-amber-700">{fmt(r.tds)}</TableCell>
                <TableCell className="text-center tabular-nums text-xs">{r.tds_pct ? `${r.tds_pct}%` : '—'}</TableCell>
                <TableCell className="text-right tabular-nums"><span className={r.balance > 1 ? 'text-red-600 font-medium' : 'text-emerald-600'}>{fmt(r.balance)}</span></TableCell>
                <TableCell className="text-center">
                  {r.not_invoiced ? (
                    <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">Not invoiced</Badge>
                  ) : r.unsettled ? (
                    <Badge className="bg-red-100 text-red-700 hover:bg-red-100 gap-1"><AlertTriangle className="w-3 h-3" />Unsettled</Badge>
                  ) : (
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1"><CheckCircle2 className="w-3 h-3" />Settled</Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <ProjectDetailModal {...{ crmProjectId: detailId, getAuthHeaders, formatCurrency, open: !!detailId, onClose: () => setDetailId(null) }} />
    </div>
  );
};

const Chip = ({ label, value, money, danger }) => (
  <div className={`rounded-xl border p-3 ${danger ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`}>
    <p className={`font-bold tabular-nums ${money ? 'text-base' : 'text-2xl'} ${danger ? 'text-red-600' : 'text-slate-900'}`}>{value}</p>
    <p className="text-xs text-slate-500 mt-0.5">{label}</p>
  </div>
);

export default EstimateInvoicePaid;
